import { useCallback, useEffect, useState } from "react";
import { Building2, Link2, Pencil } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatCurrency, formatDate, formatQuantity, unitOfMeasureAbbr } from "@/lib/format";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TH } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocStatusBadge, LineMatchBadge } from "@/components/PoDnStatus";
import { SupplierCard } from "@/components/SupplierCard";
import { useSupplier } from "@/hooks/useSupplier";
import { PurchaseOrderDialog } from "@/components/PurchaseOrdersPage";
import { stateLabel, matchStatusLabel, matchStatusVariant } from "@/lib/bill-utils";
import { toastApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";

type PurchaseOrderResponse = components["schemas"]["PurchaseOrderResponse"];
type MatchedBillView = components["schemas"]["MatchedBillView"];

export function PurchaseOrderDetailPage({
  purchaseOrderId,
  onBack,
  onMatchClick,
  onSupplierClick,
}: {
  purchaseOrderId: string;
  onBack?: () => void;
  /** Click-through for a matched bill (receives the bill id) — opens the three-way match view. */
  onMatchClick?: (billId: string) => void;
  onSupplierClick?: (supplierId: string) => void;
}) {
  const api = useDolfinClient();
  const [po, setPo] = useState<PurchaseOrderResponse | null>(null);
  const [matchedBills, setMatchedBills] = useState<MatchedBillView[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [acting, setActing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { supplier } = useSupplier(po?.supplierId);

  const fetchPo = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    const [{ data, error }, matched] = await Promise.all([
      api.GET("/v1/purchase-orders/{id}", { params: { path: { id: purchaseOrderId } } }),
      api.GET("/v1/purchase-orders/{id}/matched-bills", { params: { path: { id: purchaseOrderId } } }),
    ]);
    if (error || !data) setNotFound(true);
    else setPo(data);
    setMatchedBills(matched.data ?? []);
    setLoading(false);
  }, [api, purchaseOrderId]);

  useEffect(() => {
    fetchPo();
  }, [fetchPo]);

  const back = onBack ? <BackLink onClick={onBack}>Back to purchase orders</BackLink> : null;

  // Manual terminal transitions (the only way to reach Closed/Cancelled — matching never sets them).
  const runTransition = useCallback(
    async (action: "close" | "cancel", verb: string, call: () => Promise<{ error?: unknown }>) => {
      setActing(true);
      const { error } = await call();
      setActing(false);
      if (error) {
        toastApiError(error, `Couldn't ${action} the purchase order.`);
        return;
      }
      toast.success(`Purchase order ${verb}.`);
      fetchPo();
    },
    [fetchPo],
  );

  if (loading && !po) {
    return (
      <PageContainer>
        {back}
        <LoadingState label="Loading purchase order..." />
      </PageContainer>
    );
  }

  if (notFound || !po) {
    return (
      <PageContainer>
        {back}
        <ErrorState message="That purchase order could not be found." onRetry={fetchPo} />
      </PageContainer>
    );
  }

  const lines = [...(po.lines ?? [])].sort(
    (a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0),
  );
  const net = lines.reduce((sum, l) => sum + Number(l.subTotal ?? 0), 0);
  const isTerminal = po.status === "Closed" || po.status === "Cancelled";
  const matchedLines = lines.filter((l) => l.fullyMatched).length;
  const supplierName = supplier?.name ?? null;

  return (
    <PageContainer>
      {back}
      <PageHeader
        eyebrow="Purchase order"
        title={
          <span className="flex items-center gap-3">
            <span className="min-w-0">
              <span className="font-mono">{po.poNumber || "Purchase order"}</span>
            </span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold text-foreground tabular-nums">
              {formatCurrency(net, po.currency)}
            </span>
            <span>net</span>
            {supplierName ? (
              <>
                <span aria-hidden>·</span>
                <span>{supplierName}</span>
              </>
            ) : null}
            <span aria-hidden>·</span>
            <span>
              {lines.length} line{lines.length === 1 ? "" : "s"}
            </span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <DocStatusBadge status={po.status} />
            {!isTerminal && (
              <>
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="size-3.5" /> Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={acting}
                  onClick={() =>
                    runTransition("close", "closed", () =>
                      api.POST("/v1/purchase-orders/{id}/close", {
                        params: { path: { id: purchaseOrderId } },
                      }),
                    )
                  }
                >
                  Close
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={acting}
                  onClick={() =>
                    runTransition("cancel", "cancelled", () =>
                      api.POST("/v1/purchase-orders/{id}/cancel", {
                        params: { path: { id: purchaseOrderId } },
                      }),
                    )
                  }
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                <dt className="text-muted-foreground">Order date</dt>
                <dd>{po.orderDate ? formatDate(po.orderDate) : "-"}</dd>
                <dt className="text-muted-foreground">Expected delivery</dt>
                <dd>{po.expectedDeliveryDate ? formatDate(po.expectedDeliveryDate) : "-"}</dd>
                <dt className="text-muted-foreground">Currency</dt>
                <dd>{po.currency || "-"}</dd>
                <dt className="text-muted-foreground">External reference</dt>
                <dd className="font-mono text-xs">{po.externalRef || "-"}</dd>
                <dt className="text-muted-foreground">Created</dt>
                <dd>{formatDate(po.createdAt)}</dd>
              </dl>
            </CardContent>
          </Card>

          {po.supplierId ? (
            <SupplierCard supplierId={po.supplierId} onSupplierClick={onSupplierClick} />
          ) : (
            <EmptyState icon={Building2} title="No supplier yet">
              Assign a supplier so this order can be matched against their bills.
            </EmptyState>
          )}
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-base">Lines</CardTitle>
            <MatchProgress matched={matchedLines} total={lines.length} />
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Description</TableHead>
                    <TableHead className={TH}>Ext. ref</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Qty</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Unit price</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Net</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Matched</TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="max-w-[320px]">
                        <div className="truncate font-medium">{l.description}</div>
                        {l.sku ? (
                          <div className="truncate font-mono text-xs text-muted-foreground">SKU {l.sku}</div>
                        ) : null}
                      </TableCell>
                      {/* The integrator's own line reference, echoed so they can confirm the mapping. Not used for matching. */}
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {l.externalRef || "-"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap tabular-nums">
                        {formatQuantity(l.quantityOrdered)}
                        <span className="ml-1 text-muted-foreground">
                          {unitOfMeasureAbbr(l.unitOfMeasure) ?? l.unit ?? ""}
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(l.unitPrice, po.currency)}
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatCurrency(l.subTotal, po.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex justify-end">
                          <LineMatchBadge
                            matchedQuantity={l.matchedQuantity}
                            totalQuantity={l.quantityOrdered}
                            fullyMatched={l.fullyMatched}
                          />
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={4} className="text-right text-muted-foreground">
                      Net total
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(net, po.currency)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardContent>
        </Card>

        <MatchedBillsCard
          bills={matchedBills}
          currency={po.currency}
          onMatchClick={onMatchClick}
          emptyText="Dolfin matches this supplier's bills against the order as they arrive."
        />
      </div>

      <PurchaseOrderDialog
        open={editOpen}
        editing={po}
        onOpenChange={setEditOpen}
        onSaved={() => fetchPo()}
      />
    </PageContainer>
  );
}

export function MatchProgress({ matched, total }: { matched: number; total: number }) {
  if (total === 0) return null;
  const pct = Math.round((matched / total) * 100);
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="tabular-nums">
        {matched} of {total} line{total === 1 ? "" : "s"} matched
      </span>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div
          className={cn("h-full rounded-full", pct === 100 ? "bg-emerald-500" : "bg-accent-blue")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export function MatchedBillsCard({
  bills,
  currency,
  onMatchClick,
  emptyText,
}: {
  bills: MatchedBillView[];
  /** The view carries no currency of its own; the document's is used. */
  currency: string;
  onMatchClick?: (billId: string) => void;
  emptyText: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Matched bills</CardTitle>
      </CardHeader>
      <CardContent>
        {bills.length === 0 ? (
          <EmptyState icon={Link2} title="No bills matched yet">
            {emptyText}
          </EmptyState>
        ) : (
          <div className="space-y-2">
            {bills.map((b) => {
              const name = b.supplierName || "Supplier";
              const body = (
                <>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-medium">{b.invoiceNumber || "(no invoice number)"}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {name} · <span className="tabular-nums">{formatCurrency(b.totalAmount, currency)}</span> ·{" "}
                        {stateLabel(b.state)}
                      </div>
                    </div>
                  </div>
                  <Badge variant={matchStatusVariant(b.matchGroupStatus)}>
                    {matchStatusLabel(b.matchGroupStatus)}
                  </Badge>
                </>
              );
              const row = "flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left text-sm";
              return onMatchClick ? (
                <button
                  key={b.billId}
                  type="button"
                  onClick={() => onMatchClick(b.billId)}
                  className={cn(row, "transition hover:bg-muted/50")}
                >
                  {body}
                </button>
              ) : (
                <div key={b.billId} className={row}>
                  {body}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
